/* PILSNERISH OS — pz-pour: a liquid-chrome Lukr faucet pours liquid-gold beer into a liquid-chrome skull.
 * - Beer: CPU particle liquid (Clavet double-density relaxation), rendered as metaballs: particles splat into
 *   a half-res density buffer, a composite pass thresholds it and shades it as polished gold.
 * - Open-cranium skull (R3, geom.vessel): the stream drops into the broken cranium. The skull is one vessel
 *   with a global level h fed by the stream; each opening (mouth, nose, eye sockets, cranium rim) spills
 *   rate * kappa * (h - lip)^1.5 particles/s, so the mouth drools first, then the nose bleeds, the eyes pour,
 *   and finally the rim overflows in thin rivulets down the outside. Beer inside each opening is drawn
 *   between the back layer (interiors) and the front shell, clipped by the packed openings mask.
 * - Legacy (placeholders): the tipped-back mouth is a cup that overflows over its low corner onto the jaw.
 * - Outflow runs down the chrome as a viscous film, drips off and lands in a pool along the bottom of the
 *   frame: a 1D heightfield (level + waves) fed by landing drops, drawn as beer with a foam line, capped.
 * - Colliders (cursor + dragged floaters) block the stream and throw spray. shoot(x, y): a T-1000 bullet
 *   hole on chrome (crater, bright rim, ripple, silver splash, heals in ~2.5s), a splash on the beer.
 * API: PZPour.create(opts) -> { layout(geom), setColliders(list), shoot(x, y), hit(x, y), start(), stop(),
 *   settle(seconds), state(), destroy() } or null when WebGL is unavailable.
 *   PZPour.analyze(backImg, frontImg, tilt, anchors) -> rotated skull geometry in sprite-height units (legacy).
 *   PZPour.analyzeOpen(backImg, frontImg, openingsImg, anchorsJson) -> open-cranium skull geometry (sprite uv).
 */
(function () {
  'use strict';
  var w = window, d = document;
  if (w.PZPour) return;

  // K_POUR: open-cranium outflow that left the chrome, falls clean (no clumping) and draws with a short trail
  // K_FIZZ: carbonation inside the open skull: rises through the beer behind the front shell, pops or hops at the surface
  var K_STREAM = 0, K_FREE = 1, K_SILVER = 2, K_FILM = 3, K_POUR = 4, K_FIZZ = 5;
  function rnd(a, b) { return a + Math.random() * (b - a); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* ------------------------------------------------------------------ alpha maps + skull geometry */
  function alphaMap(img, size) {
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height; // <img> or ImageBitmap
    var s = size / Math.max(iw, ih);
    var cw = Math.max(1, Math.round(iw * s)), ch = Math.max(1, Math.round(ih * s));
    var c = d.createElement('canvas'); c.width = cw; c.height = ch;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, cw, ch);
    var data = ctx.getImageData(0, 0, cw, ch).data, a = new Uint8Array(cw * ch);
    for (var i = 0; i < a.length; i++) a[i] = data[i * 4 + 3];
    return { w: cw, h: ch, a: a };
  }
  // mouth hole for sprites without a separate mouth layer: an ellipse at the mouth anchor
  function ellipseMap(wd, ht, m) {
    var a = new Uint8Array(wd * ht), cx = m[0] * wd, cy = m[1] * ht, rx = (m[2] || 0.12) * wd, ry = rx * 0.6;
    for (var j = 0; j < ht; j++) for (var i = 0; i < wd; i++) {
      var dx = (i + 0.5 - cx) / rx, dy = (j + 0.5 - cy) / ry;
      if (dx * dx + dy * dy < 1) a[j * wd + i] = 255;
    }
    return { w: wd, h: ht, a: a };
  }
  function sampleMap(am, u, v) {
    if (!am || u < 0 || v < 0 || u >= 1 || v >= 1) return 0;
    return am.a[((v * am.h) | 0) * am.w + ((u * am.w) | 0)];
  }
  // Rotated skull geometry, in units of the sprite height, relative to the sprite centre (tilt > 0 = CCW).
  function analyze(back, front, tilt, anch) {
    var amF = alphaMap(front, 256);
    var amB = back ? alphaMap(back, 256) : ellipseMap(amF.w, amF.h, (anch && anch.mouth) || [0.5, 0.78, 0.13]);
    var asp = front.naturalWidth / front.naturalHeight, c = Math.cos(tilt), s = Math.sin(tilt);
    var fw = amF.w, fh = amF.h, i, j, k, lx, ly, X, Y;
    var bx0 = 1e9, by0 = 1e9, bx1 = -1e9, by1 = -1e9, BIN = 0.004, colTop = {};
    var hole = [], hb = -1e9, corner = null, best = -1e9;
    for (j = 0; j < fh; j++) {
      for (i = 0; i < fw; i++) {
        var aF = amF.a[j * fw + i], aB = sampleMap(amB, (i + 0.5) / fw, (j + 0.5) / fh);
        if (aF < 128 && aB < 128) continue;
        lx = ((i + 0.5) / fw - 0.5) * asp; ly = (j + 0.5) / fh - 0.5;
        X = lx * c + ly * s; Y = -lx * s + ly * c;
        if (X < bx0) bx0 = X; if (X > bx1) bx1 = X; if (Y < by0) by0 = Y; if (Y > by1) by1 = Y;
        k = Math.round(X / BIN);
        if (!(k in colTop) || colTop[k] > Y) colTop[k] = Y;
        if (aB >= 128) {
          hole.push(X, Y);
          if (Y > hb) hb = Y;
          if (Y - X > best) { best = Y - X; corner = [X, Y]; } // lower-left corner of the mouth = the low side
        }
      }
    }
    if (!corner) return null;
    // stream target: inside the part of the mouth that holds liquid (below the corner's level)
    var right = corner[0];
    for (k = 0; k < hole.length; k += 2) if (Math.abs(hole[k + 1] - corner[1]) < 0.012 && hole[k] > right) right = hole[k];
    var tx = corner[0] + (right - corner[0]) * 0.26, ht = 1e9, lx0 = 0, ln = 0;
    // front of the lower lip: the lowest part of the mouth opening (the chin side)
    for (k = 0; k < hole.length; k += 2) if (hole[k + 1] > hb - 0.012) { lx0 += hole[k]; ln++; }
    for (k = 0; k < hole.length; k += 2) if (Math.abs(hole[k] - tx) < 0.006 && hole[k + 1] < ht) ht = hole[k + 1];
    var st = null, kb = Math.round(tx / BIN);
    for (k = 0; k < 4 && st == null; k++) st = colTop[kb + k] != null ? colTop[kb + k] : (colTop[kb - k] != null ? colTop[kb - k] : null);
    return {
      tilt: tilt, asp: asp, back: amB, front: amF,
      box: [bx0, by0, bx1, by1], corner: corner, holeBottom: hb, lip: [ln ? lx0 / ln : corner[0], hb],
      target: [tx, ht < 1e8 ? ht : corner[1]], silTop: st != null ? st : by0
    };
  }

  /* ------------------------------------------------------------------ open-cranium skull (R3) */
  function rgbMap(img, size) {
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    var s = size / Math.max(iw, ih);
    var cw = Math.max(1, Math.round(iw * s)), ch = Math.max(1, Math.round(ih * s));
    var c = d.createElement('canvas'); c.width = cw; c.height = ch;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, cw, ch);
    var data = ctx.getImageData(0, 0, cw, ch).data, n = cw * ch, r = new Uint8Array(n), g = new Uint8Array(n), b = new Uint8Array(n);
    for (var i = 0; i < n; i++) { r[i] = data[i * 4]; g[i] = data[i * 4 + 1]; b[i] = data[i * 4 + 2]; }
    return { w: cw, h: ch, r: r, g: g, b: b };
  }
  // R4 packed maps (one small PNG): R = shell alpha, G = interiors alpha, B = opening code (64 cranium, 128 eyes,
  // 192 nose + mouth) -> the same {front, back, ops} maps the full-sprite readbacks produced, at a fraction of the cost
  function packedMaps(img) {
    var c = d.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    var data = ctx.getImageData(0, 0, c.width, c.height).data, n = c.width * c.height;
    var F = new Uint8Array(n), B = new Uint8Array(n), r = new Uint8Array(n), g = new Uint8Array(n), b = new Uint8Array(n);
    for (var i = 0; i < n; i++) {
      F[i] = data[i * 4]; B[i] = data[i * 4 + 1];
      var k = data[i * 4 + 2];
      if (k > 32 && k < 96) r[i] = 255; else if (k >= 96 && k < 160) g[i] = 255; else if (k >= 160) b[i] = 255;
    }
    return { front: { w: c.width, h: c.height, a: F }, back: { w: c.width, h: c.height, a: B }, ops: { w: c.width, h: c.height, r: r, g: g, b: b } };
  }
  // opening under sprite uv: 'cranium' | 'eye_l' | 'eye_r' | 'nose' | 'mouth' | null
  function openingAt(AN, u, v) {
    var om = AN.ops;
    if (!om || u < 0 || v < 0 || u >= 1 || v >= 1) return null;
    var k = ((v * om.h) | 0) * om.w + ((u * om.w) | 0);
    if (om.r[k] > 127) return 'cranium';
    if (om.g[k] > 127) return u < AN.split[0] ? 'eye_l' : 'eye_r';
    if (om.b[k] > 127) return v < AN.split[1] ? 'nose' : 'mouth';
    return null;
  }
  // Geometry of the unrotated open-cranium sprite in sprite uv (0..1): openings with their spill points,
  // rim rivulet sources along the front rim, the stream entry, per-row silhouette extent (headline clearance).
  function analyzeOpen(back, front, opImg, j, mapsImg) {
    var S = j.skull, O = S.openings, L = S.level_model || {};
    var pm = mapsImg ? packedMaps(mapsImg) : null;
    var amF = pm ? pm.front : (front ? alphaMap(front, 256) : null), amB = pm ? pm.back : (back ? alphaMap(back, 256) : null);
    var om = pm ? pm.ops : (opImg ? rgbMap(opImg, 320) : null);
    var pre = !!(S.row_min && S.split && S.sil && O.cranium && O.cranium.rivulets); // R4 anchors: mask analysis done offline
    // geometry-only call (no images yet) needs the precomputed analysis; a later call attaches the pixel maps
    if (!amF && !pre) return null;
    var asp = front && !pm ? (front.naturalWidth || front.width) / (front.naturalHeight || front.height) : S.w / S.h, fw = amF ? amF.w : 0, fh = amF ? amF.h : 0, i, jj;
    var rowMin = new Float32Array(pre ? S.row_min.length : fh), rowMax = new Float32Array(rowMin.length), x0 = 1, y0 = 1, x1 = 0, y1 = 0;
    if (pre) { rowMin.set(S.row_min); rowMax.set(S.row_max || S.row_min); x0 = S.sil[0]; y0 = S.sil[1]; x1 = S.sil[2]; y1 = S.sil[3]; }
    for (jj = 0; jj < fh && !pre; jj++) {
      rowMin[jj] = 2; rowMax[jj] = -1;
      for (i = 0; i < fw; i++) {
        var k = jj * fw + i;
        if (amF.a[k] < 128 && (!amB || amB.a[k] < 128)) continue;
        var u = (i + 0.5) / fw, v = (jj + 0.5) / fh;
        if (u < rowMin[jj]) rowMin[jj] = u; if (u > rowMax[jj]) rowMax[jj] = u;
        if (u < x0) x0 = u; if (u > x1) x1 = u; if (v < y0) y0 = v; if (v > y1) y1 = v;
      }
    }
    var AN = { open: true, tilt: 0, asp: asp, back: amB, front: amF, ops: om, box: [-asp / 2, -0.5, asp / 2, 0.5], sil: [x0, y0, x1, y1] };
    AN.split = pre ? S.split.slice() : [((O.eye_l ? O.eye_l.bbox[2] : 0.3) + (O.eye_r ? O.eye_r.bbox[0] : 0.3)) / 2,
      O.nose && O.mouth ? (O.nose.bbox[3] + O.mouth.bbox[1]) / 2 : 0];
    AN.rowMin = rowMin; AN.rowMax = rowMax;
    AN.entry = [S.entry.x, S.entry.top, S.entry.bottom];
    var kap = L.kappa || {}, kex = L.exponent || {}, kcap = L.head_cap || {};
    // lowest boundary of one opening per mask column: [u, v] pairs within `tol` of the opening's lowest point
    function bottomEdge(key, tol) {
      var out = [], lo = -1;
      if (!om) return out;
      var ow = om.w, oh = om.h, a, b2;
      for (a = 0; a < ow; a++) {
        var u = (a + 0.5) / ow, best = -1;
        for (b2 = oh - 1; b2 >= 0; b2--) if (openingAt(AN, u, (b2 + 0.5) / oh) === key) { best = (b2 + 0.5) / oh; break; }
        if (best >= 0) { out.push([u, best]); if (best > lo) lo = best; }
      }
      return out.filter(function (p) { return p[1] >= lo - tol; });
    }
    var ops = [];
    ['mouth', 'nose', 'eye_l', 'eye_r'].forEach(function (key) {
      var o = O[key];
      if (!o) return;
      var tol = key === 'mouth' ? 0.009 : (key === 'nose' ? 0.005 : 0.006);
      var edge = o.spills ? o.spills.map(function (p) { return [p[0], p[1] - 0.006]; }) : bottomEdge(key, tol);
      if (!edge.length) edge = [[o.lip[0], o.lip[1]]];
      ops.push({
        key: key, hLip: o.h_lip, hFull: o.h_full, appear: o.appear_y, full: o.full_y, kappa: kap[key] != null ? kap[key] : 0.4,
        exp: kex[key] || 1, cap: kcap[key] || 9, spills: edge.map(function (p) { return [p[0], p[1] + 0.006, 1]; }), bbox: o.bbox
      });
    });
    // cranium: rivulets over the front rim = the lowest boundary of the opening, several thin sources
    var cr = O.cranium, rim = cr.rivulets ? [] : bottomEdge('cranium', 1), riv = cr.rivulets ? cr.rivulets.map(function (p) { return p.slice(); }) : [];
    if (rim.length) {
      var ua = rim[0][0], ub = rim[rim.length - 1][0], n = 4;
      for (i = 0; i < n; i++) {
        var t = (i + 0.5) / n, uu = ua + (ub - ua) * t, bestP = null, bd = 1;
        rim.forEach(function (p) { var dd = Math.abs(p[0] - uu); if (dd < bd) { bd = dd; bestP = p; } });
        if (bestP) riv.push([bestP[0], bestP[1] + 0.007, 1]);
      }
      (cr.notches || []).forEach(function (p) {
        var near = null;
        riv.forEach(function (r) { if (Math.abs(r[0] - p[0]) < 0.045) near = r; });
        if (near) { near[0] = p[0]; near[1] = p[1] + 0.007; near[2] = 1.6; } else riv.push([p[0], p[1] + 0.007, 1.6]);
      });
    }
    ops.push({
      key: 'cranium', hLip: cr.h_lip != null ? cr.h_lip : 1, hFull: cr.h_full || 1, appear: cr.appear_y, full: cr.full_y,
      kappa: kap.cranium != null ? kap.cranium : 6, exp: kex.cranium || 1.5, cap: kcap.cranium || 9, spills: riv, bbox: cr.bbox
    });
    AN.openings = ops;
    AN.level = { segs: L.segments || [[0, 17], [0.1, 9], [0.75, 20], [1, 2]], hSteady: L.h_steady || 1.08 };
    return AN;
  }
  // level <-> volume (seconds of nominal inflow), piecewise-linear capacity
  function volOf(segs, h) {
    var v = 0;
    for (var i = 0; i < segs.length; i++) {
      var h0 = segs[i][0], h1 = i + 1 < segs.length ? segs[i + 1][0] : 1e9;
      if (h <= h0) break;
      v += (Math.min(h, h1) - h0) * segs[i][1];
    }
    return v;
  }
  function hOf(segs, v) {
    for (var i = 0; i < segs.length; i++) {
      var h0 = segs[i][0], h1 = i + 1 < segs.length ? segs[i + 1][0] : 1e9, cap = (h1 - h0) * segs[i][1];
      if (v <= cap || i === segs.length - 1) return h0 + v / segs[i][1];
      v -= cap;
    }
    return 0;
  }

  /* ------------------------------------------------------------------ simulation */
  function Sim(max) {
    this.max = max;
    var F = Float32Array;
    this.x = new F(max); this.y = new F(max); this.px = new F(max); this.py = new F(max);
    this.vx = new F(max); this.vy = new F(max);
    this.floorT = new F(max); this.life = new F(max); this.size = new F(max); this.poolLife = new F(max);
    this.fv = new F(max); this.rad = new F(max); // film speed cap factor, splat radius factor (thin rivulets)
    this.op = new Int8Array(max); // K_FIZZ: index of the opening it rises in
    this.alive = new Uint8Array(max); this.kind = new Uint8Array(max); this.hitFloor = new Uint8Array(max);
    this.free = [];
    for (var i = max - 1; i >= 0; i--) this.free.push(i);
    this.idx = new Int32Array(max); this.n = 0;
    this.cellOf = new Int32Array(max); this.sorted = new Int32Array(max);
    this.cellStart = new Int32Array(1); this.gw = 1; this.gh = 1;
    this.colliders = [];
    this.emitAcc = 0;
    this.time = 0;
    this.g = {};
    this.masks = null; this.mouth = null; this.pool = null; this.vessel = null;
  }
  Sim.prototype.spawn = function (x, y, vx, vy, kind, life) {
    if (!this.free.length) return -1;
    var i = this.free.pop();
    this.alive[i] = 1; this.kind[i] = kind; this.hitFloor[i] = 0;
    this.x[i] = x; this.y[i] = y; this.vx[i] = vx; this.vy[i] = vy; this.px[i] = x; this.py[i] = y;
    this.floorT[i] = 0; this.life[i] = life || 0; this.size[i] = kind === K_SILVER ? 1 : (kind === K_FILM ? 0.62 : 0.35);
    this.poolLife[i] = rnd(1.2, 2.2); this.fv[i] = 1; this.rad[i] = 1;
    return i;
  };
  Sim.prototype.kill = function (i) { if (this.alive[i]) { this.alive[i] = 0; this.free.push(i); } };

  // open-cranium vessel: level from the stored volume, waterline (stage y) of each opening
  Sim.prototype.vLevel = function () {
    var V = this.vessel;
    return V ? hOf(V.segs, V.vol / V.rate) : 0;
  };
  Sim.prototype.vWater = function (o, h) {
    if (o.key === 'cranium') {
      var f = Math.pow(clamp(h / Math.max(0.1, o.hFull), 0, 1), 0.85);
      return h <= 0.002 ? 1e5 : o.yA + (o.yF - o.yA) * f;
    }
    if (h < o.hLip) return 1e5;
    return o.yA + (o.yF - o.yA) * clamp((h - o.hLip) / Math.max(0.01, o.hFull - o.hLip), 0, 1);
  };
  // outflow: every opening above its lip spills film over its lowest edge (rim: thin rivulets)
  Sim.prototype.vesselFlow = function (dt) {
    var V = this.vessel, S = this.g.S;
    if (!V) return;
    var h = V.h = this.vLevel();
    for (var k = 0; k < V.ops.length; k++) {
      var o = V.ops[k];
      o.wl = this.vWater(o, h);
      var head = h - o.hLip;
      if (head <= 0 || !o.spills.length) { o.acc = 0; continue; }
      o.acc += V.rate * o.kappa * Math.pow(Math.min(o.cap, head), o.exp) * dt;
      while (o.acc >= 1 && V.vol >= 1) {
        o.acc -= 1; V.vol -= 1; o.out++;
        var r0 = Math.random() * o.wsum, sp = o.spills[0];
        for (var q = 0; q < o.spills.length; q++) { r0 -= o.spills[q].w; if (r0 <= 0) { sp = o.spills[q]; break; } }
        var i = o.pour
          ? this.spawn(sp.x + rnd(-1, 1) * o.jit * S, sp.y + rnd(-0.8, 0.8) * S, (o.vx + rnd(-3, 3)) * S, rnd(70, 110) * S, K_POUR, 0)
          : this.spawn(sp.x + rnd(-1, 1) * o.jit * S, sp.y + rnd(-0.8, 0.8) * S, (o.vx + rnd(-3, 3)) * S, rnd(8, 20) * S, K_FILM, 0);
        if (i >= 0) { this.fv[i] = o.fv; this.rad[i] = o.rad; this.size[i] = 0.9; this.life[i] = sp.x / S; }
      }
    }
  };
  // carbonation: bubbles spawn anywhere in the visible beer of each opening (rate ~ visible area) and rise
  Sim.prototype.vesselFizz = function (dt) {
    var V = this.vessel, S = this.g.S;
    if (!V || !V.an) return;
    for (var k = 0; k < V.ops.length; k++) {
      var o = V.ops[k], bb = o.bb;
      if (!bb || o.wl >= bb[3] - 2 * S) { o.fz = 0; continue; }
      var top = Math.max(o.wl + 3 * S, bb[1]), area = (bb[3] - top) * (bb[2] - bb[0]) * 0.6 / (S * S);
      o.fz = (o.fz || 0) + area * 0.0075 * (o.key === 'mouth' ? 1.5 : 1) * (this.g.mobile ? 0.5 : 1) * dt;
      while (o.fz >= 1) {
        o.fz -= 1;
        for (var tr = 0; tr < 3; tr++) {
          var x = bb[0] + Math.random() * (bb[2] - bb[0]), y = top + Math.random() * (bb[3] - top);
          if (openingAt(V.an, (x - V.sk[0]) / V.sk[2], (y - V.sk[1]) / V.sk[3]) !== o.key) continue;
          var i = this.spawn(x, y, 0, -rnd(70, 170) * S, K_FIZZ, 0);
          if (i >= 0) { this.op[i] = k; this.life[i] = rnd(0, 6.28); this.rad[i] = rnd(0.3, 0.58); this.size[i] = 1; this.fv[i] = 1; }
          break;
        }
      }
    }
  };
  // rising bubble (fv 1): wobbles up, pops at the surface or (22%) hops above it and falls back in (fv 2)
  Sim.prototype.fizzStep = function (i, dt) {
    var V = this.vessel, S = this.g.S, o = V.ops[this.op[i]];
    if (!o || o.wl > 9e4) { this.kill(i); return; }
    var x = this.x, y = this.y;
    this.px[i] = x[i]; this.py[i] = y[i];
    if (this.fv[i] < 1.5) {
      this.vx[i] = Math.sin(this.time * 7 + this.life[i]) * 16 * S;
      this.vy[i] = Math.max(this.vy[i] - 90 * S * dt, -240 * S);
      x[i] += this.vx[i] * dt; y[i] += this.vy[i] * dt;
      if (o.bb && (x[i] < o.bb[0] || x[i] > o.bb[2])) { this.kill(i); return; }
      if (y[i] <= o.wl + 1.2 * S) {
        if (Math.random() < 0.22) { this.fv[i] = 2; this.vy[i] = -rnd(60, 150) * S; this.vx[i] = rnd(-45, 45) * S; this.rad[i] *= 0.8; }
        else this.kill(i);
      }
    } else {
      this.vy[i] += 1900 * S * dt;
      x[i] += this.vx[i] * dt; y[i] += this.vy[i] * dt;
      if (this.vy[i] > 0 && y[i] > o.wl + 2 * S) this.kill(i);
    }
  };

  // mouth: liquid level (stage y) for the current fill
  Sim.prototype.mouthLevel = function () {
    var M = this.mouth;
    if (!M) return 1e9;
    var f = M.cap ? clamp(M.vol / M.cap, 0, 1) : 0;
    return M.bottomY - (M.bottomY - M.spillY) * Math.pow(f, 0.7);
  };
  // pool: current extent + level from the stored volume
  Sim.prototype.poolShape = function () {
    var P = this.pool;
    if (!P) return;
    var v = P.cap ? clamp(P.vol / P.cap, 0, 1) : 0;
    P.v = v;
    var grow = v > 0 ? clamp(0.12 + 0.88 * Math.sqrt(Math.min(1, v * 1.7)), 0, 1) : 0;
    P.curL = P.cx - (P.cx - P.x0) * grow; P.curR = P.cx + (P.x1 - P.cx) * grow;
    P.level = v > 0 ? Math.max(2.2 * this.g.S, P.capH * v) : 0;
  };
  Sim.prototype.poolEdge = function (x) {
    var P = this.pool;
    if (!P || !P.level || x <= P.curL || x >= P.curR) return 0;
    // puddle ends: a long, soft meniscus taper
    var ew = Math.max(6, Math.min(70 * this.g.S, (P.curR - P.curL) * 0.22));
    var dl = P.curL <= P.x0 + 1 ? 1e9 : x - P.curL, dr = P.curR >= P.x1 - 1 ? 1e9 : P.curR - x;
    var t = Math.min(dl, dr) / ew;
    if (t >= 1) return 1;
    var sm = t * t * (3 - 2 * t);
    return 0.12 + 0.88 * Math.sqrt(sm);
  };
  Sim.prototype.poolSurf = function (x) {
    var P = this.pool, e = this.poolEdge(x);
    if (!e) return 0;
    var i = clamp(Math.round((x - P.x0) / P.dx), 0, P.N - 1);
    return Math.max(0, (P.level + P.h[i]) * e);
  };
  Sim.prototype.poolKick = function (x, amt) {
    var P = this.pool;
    if (!P) return;
    var i = Math.round((x - P.x0) / P.dx);
    for (var k = -2; k <= 2; k++) {
      var j = i + k;
      if (j >= 0 && j < P.N) P.vv[j] += amt * (k ? (k === 1 || k === -1 ? 0.6 : 0.25) : 1);
    }
  };

  Sim.prototype.step = function (dt) {
    var g = this.g, S = g.S;
    if (!S) return;
    this.time += dt;
    var x = this.x, y = this.y, px = this.px, py = this.py, vx = this.vx, vy = this.vy, al = this.alive, kind = this.kind;
    var MK = this.masks, M = this.mouth, P = this.pool;
    var i, j, k, max = this.max;
    var H = 15 * S, H2 = H * H, RHO0 = 4.2, K = 0.5 * S, KN = 1.6 * S, G = 1900 * S, VMAX = 1250 * S, FILMV = 125 * S;

    // emit at the spout
    if (g.emit) {
      this.emitAcc += dt * g.rate;
      while (this.emitAcc >= 1) {
        this.emitAcc -= 1;
        this.spawn(g.sx + rnd(-0.35, 0.35) * g.sw, g.sy + rnd(0, 4) * S, rnd(-1.5, 1.5) * S, (150 + rnd(0, 12)) * S, K_STREAM, 0);
      }
    }
    var VS = this.vessel;
    if (VS) { this.vesselFlow(dt); this.vesselFizz(dt); }
    // overflow: the full mouth spills over its low corner onto the jaw
    if (M && M.ovAcc >= 1) {
      while (M.ovAcc >= 1) {
        M.ovAcc -= 1;
        var sp0 = M.spills[0], r0 = Math.random(), wacc = 0;
        for (k = 0; k < M.spills.length; k++) { wacc += M.spills[k].w; if (r0 <= wacc) { sp0 = M.spills[k]; break; } }
        this.spawn(sp0.x + rnd(-2.5, 2.5) * S, sp0.y + rnd(-1.5, 1.5) * S, (sp0.vx + rnd(-8, 8)) * S, rnd(10, 35) * S, K_FILM, 0);
      }
    }
    // integrate
    var n = 0;
    for (i = 0; i < max; i++) {
      if (!al[i]) continue;
      if (kind[i] === K_FIZZ) { if (VS) this.fizzStep(i, dt); else this.kill(i); continue; }
      this.idx[n++] = i;
      if (kind[i] === K_FILM) {
        if (MK && MK.at(MK.front, x[i], y[i])) {
          // viscous film running down the front of the jaw
          vy[i] += G * 0.4 * dt;
          if (vy[i] > FILMV * this.fv[i]) vy[i] = FILMV * this.fv[i];
          if (VS) vx[i] = vx[i] * 0.85 + 0.15 * Math.sin(y[i] * 0.036 / S + this.life[i] * 0.37) * 7 * S;
          else vx[i] = vx[i] * 0.9 + rnd(-1, 1) * 4 * S * dt;
        } else {
          kind[i] = VS ? K_POUR : K_FREE; // dripped off the chrome
          vy[i] += G * dt;
        }
      } else {
        vy[i] += G * dt;
        if (vy[i] > VMAX) vy[i] = VMAX;
      }
      if (this.hitFloor[i]) vx[i] *= 0.985;
      px[i] = x[i]; py[i] = y[i];
      x[i] += vx[i] * dt; y[i] += vy[i] * dt;
      if (this.size[i] < 1 && this.floorT[i] < this.poolLife[i]) this.size[i] = Math.min(1, this.size[i] + dt * 5);
    }
    this.n = n;

    // spatial grid
    var gw = this.gw = Math.max(1, Math.ceil(g.W / H) + 2), gh = this.gh = Math.max(1, Math.ceil((g.H + 200 * S) / H) + 2);
    var nc = gw * gh;
    if (this.cellStart.length < nc + 1) this.cellStart = new Int32Array(nc + 1);
    var cs = this.cellStart;
    for (k = 0; k <= nc; k++) cs[k] = 0;
    var yOff = 200 * S;
    for (k = 0; k < n; k++) {
      i = this.idx[k];
      var cx = Math.floor(x[i] / H) + 1, cy = Math.floor((y[i] + yOff) / H) + 1;
      cx = cx < 0 ? 0 : cx >= gw ? gw - 1 : cx; cy = cy < 0 ? 0 : cy >= gh ? gh - 1 : cy;
      var c = cy * gw + cx;
      this.cellOf[i] = c; cs[c + 1]++;
    }
    for (k = 0; k < nc; k++) cs[k + 1] += cs[k];
    var fill = new Int32Array(cs.subarray(0, nc));
    for (k = 0; k < n; k++) { i = this.idx[k]; this.sorted[fill[this.cellOf[i]]++] = kind[i] !== K_SILVER && kind[i] !== K_FIZZ ? i : -1; }

    // double density relaxation (pressure + near pressure) -> cohesive, splashy liquid (not the clean stream)
    for (k = 0; k < n; k++) {
      i = this.idx[k];
      if (kind[i] === K_SILVER || kind[i] === K_STREAM || kind[i] === K_POUR || kind[i] === K_FIZZ || (VS && kind[i] === K_FILM)) continue;
      var c0 = this.cellOf[i], ccx = c0 % gw, ccy = (c0 / gw) | 0;
      var rho = 0, rhoN = 0, a, b, cc, s, e, jj, dx, dy, r2, r, q;
      for (b = ccy - 1; b <= ccy + 1; b++) {
        if (b < 0 || b >= gh) continue;
        for (a = ccx - 1; a <= ccx + 1; a++) {
          if (a < 0 || a >= gw) continue;
          cc = b * gw + a; s = cs[cc]; e = cs[cc + 1];
          for (jj = s; jj < e; jj++) {
            j = this.sorted[jj];
            if (j < 0 || j === i) continue;
            dx = x[j] - x[i]; dy = y[j] - y[i]; r2 = dx * dx + dy * dy;
            if (r2 >= H2) continue;
            q = 1 - Math.sqrt(r2) / H;
            rho += q * q; rhoN += q * q * q;
          }
        }
      }
      var Pp = K * (rho - RHO0), PN = KN * rhoN, ddx = 0, ddy = 0;
      if (Pp < -K * 1.5) Pp = -K * 1.5;
      for (b = ccy - 1; b <= ccy + 1; b++) {
        if (b < 0 || b >= gh) continue;
        for (a = ccx - 1; a <= ccx + 1; a++) {
          if (a < 0 || a >= gw) continue;
          cc = b * gw + a; s = cs[cc]; e = cs[cc + 1];
          for (jj = s; jj < e; jj++) {
            j = this.sorted[jj];
            if (j < 0 || j === i) continue;
            dx = x[j] - x[i]; dy = y[j] - y[i]; r2 = dx * dx + dy * dy;
            if (r2 >= H2 || r2 < 1e-6) continue;
            r = Math.sqrt(r2); q = 1 - r / H;
            var D = (Pp * q + PN * q * q) * 0.5, ux = dx / r, uy = dy / r;
            x[j] += D * ux; y[j] += D * uy; ddx -= D * ux; ddy -= D * uy;
          }
        }
      }
      x[i] += ddx; y[i] += ddy;
    }

    // collisions, mouth, pool, floor, lifetimes, velocities
    var cols = this.colliders, floor = g.floorY, invdt = 1 / dt, level = this.mouthLevel();
    if (P) this.poolShape();
    for (k = 0; k < n; k++) {
      i = this.idx[k];
      if (!al[i]) continue;
      var vxi = (x[i] - px[i]) * invdt, vyi = (y[i] - py[i]) * invdt;
      for (var ci = 0; ci < cols.length; ci++) {
        var C = cols[ci], ex = x[i] - C.x, ey = y[i] - C.y, er2 = ex * ex + ey * ey;
        if (er2 >= C.r * C.r) continue;
        var er = Math.sqrt(er2) || 0.001, nx = ex / er, ny = ey / er;
        x[i] = C.x + nx * C.r; y[i] = C.y + ny * C.r;
        var vn = vxi * nx + vyi * ny;
        var rest = 0.35 + Math.random() * 0.5;
        if (vn < 0) { vxi -= (1 + rest) * vn * nx; vyi -= (1 + rest) * vn * ny; }
        var tang = rnd(-0.6, 0.6) * Math.abs(vn);
        vxi += (C.vx || 0) * 0.9 - ny * tang; vyi += (C.vy || 0) * 0.9 + nx * tang;
        if (kind[i] === K_STREAM || kind[i] === K_FILM || kind[i] === K_POUR) kind[i] = K_FREE;
      }
      // open cranium: the stream drops behind the front rim into the beer (or the empty skull)
      if (VS && kind[i] === K_STREAM && y[i] >= Math.min(VS.cr.wl, VS.cr.yA)) {
        this.kill(i);
        VS.vol += 1; VS.hits += 1; VS.ripX = x[i];
        continue;
      }
      // the stream goes into the mouth: behind the upper teeth, then into the liquid (or down the throat)
      if (kind[i] === K_STREAM && MK && M && y[i] > MK.holeTopAt(x[i])) {
        if (y[i] >= level - 1 || !MK.at(MK.hole, x[i], y[i])) {
          this.kill(i);
          M.vol += 1; M.hits += 1;
          if (M.vol > M.cap) { M.vol = M.cap; M.ovAcc += M.ratio; }
          continue;
        }
      }
      if (kind[i] !== K_SILVER) {
        var surf = P ? this.poolSurf(x[i]) : 0, inPool = P && x[i] > P.x0 && x[i] < P.x1;
        if (y[i] >= floor - surf) {
          if (inPool) {
            // land in the pool: a few splash droplets bounce, the rest joins the beer
            if (!this.hitFloor[i] && vyi > 320 * S && Math.random() < 0.18) {
              this.hitFloor[i] = 1;
              y[i] = floor - surf - 1;
              vyi = -vyi * rnd(0.12, 0.3); vxi += rnd(-0.18, 0.18) * Math.abs(vyi) * 3;
              this.size[i] *= 0.6; kind[i] = K_FREE;
              this.poolKick(x[i], -Math.min(1.2 * S, 0.0015 * Math.abs(vyi)));
            } else {
              this.poolKick(x[i], -Math.min(0.8 * S, 0.0011 * Math.max(0, vyi)));
              this.kill(i);
              P.vol = Math.min(P.cap, P.vol + 1);
              continue;
            }
          } else {
            // outside the pool: splat on the floor and fade
            y[i] = floor;
            if (!this.hitFloor[i] && vyi > 380 * S) {
              var sp = vyi;
              vyi = -sp * rnd(0.04, 0.3);
              vxi += rnd(-0.3, 0.3) * sp;
              if (vxi > 520 * S) vxi = 520 * S; else if (vxi < -520 * S) vxi = -520 * S;
            } else { vyi = 0; vxi *= 0.8; }
            this.hitFloor[i] = 1;
            if (kind[i] === K_STREAM || kind[i] === K_FILM || kind[i] === K_POUR) kind[i] = K_FREE;
          }
        }
        if (this.hitFloor[i] && !inPool && y[i] > floor - 30 * S) {
          this.floorT[i] += dt * 1.6;
          if (this.floorT[i] > this.poolLife[i]) { this.size[i] -= dt * 1.6; if (this.size[i] <= 0.05) { this.kill(i); continue; } }
        }
      } else {
        this.life[i] -= dt;
        if (this.life[i] <= 0 || y[i] > floor) { this.kill(i); continue; }
        this.size[i] = Math.min(1, this.life[i] * 2);
      }
      if (x[i] < g.minX || x[i] > g.maxX || y[i] > g.H + 80 * S || y[i] < -300 * S) { this.kill(i); continue; }
      vx[i] = vxi; vy[i] = vyi;
    }

    // pool surface: 1D waves on top of the level
    if (P && P.N > 2) {
      var h = P.h, vv = P.vv, N = P.N, c2 = P.c2, hm = 0.3 * P.level + S;
      for (i = 0; i < N; i++) {
        var lap = (i > 0 ? h[i - 1] : h[i]) + (i < N - 1 ? h[i + 1] : h[i]) - 2 * h[i];
        vv[i] = (vv[i] + c2 * lap) * 0.972 - h[i] * 0.006;
      }
      for (i = 0; i < N; i++) { h[i] += vv[i]; if (h[i] > hm) h[i] = hm; else if (h[i] < -hm) h[i] = -hm; }
    }
    if (M) M.rip = M.rip * Math.pow(0.02, dt) + Math.min(1, M.hits / 3) * (1 - Math.pow(0.02, dt));
    if (M) M.hits = 0;
    if (VS) { VS.rip = VS.rip * Math.pow(0.02, dt) + Math.min(1, VS.hits / 3) * (1 - Math.pow(0.02, dt)); VS.hits = 0; }
  };

  Sim.prototype.burst = function (cx, cy, rad, power) {
    var hit = 0;
    for (var i = 0; i < this.max; i++) {
      if (!this.alive[i] || this.kind[i] === K_SILVER || this.kind[i] === K_FIZZ) continue;
      var dx = this.x[i] - cx, dy = this.y[i] - cy, r = Math.sqrt(dx * dx + dy * dy);
      if (r > rad) continue;
      var f = (1 - r / rad) * power * rnd(0.6, 1.4), a = Math.atan2(dy, dx) + rnd(-0.6, 0.6);
      this.vx[i] += Math.cos(a) * f; this.vy[i] += Math.sin(a) * f - power * 0.25;
      this.kind[i] = K_FREE; hit++;
    }
    return hit;
  };
  Sim.prototype.nearest = function (cx, cy, rad) {
    var best = rad * rad, found = false;
    for (var i = 0; i < this.max; i++) {
      if (!this.alive[i] || this.kind[i] === K_SILVER || this.kind[i] === K_FIZZ) continue;
      var dx = this.x[i] - cx, dy = this.y[i] - cy, r2 = dx * dx + dy * dy;
      if (r2 < best) { best = r2; found = true; }
    }
    return found;
  };

  /* ------------------------------------------------------------------ GL */
  var VS_SPLAT = [
    'attribute vec4 a;',
    'uniform vec2 uRes;uniform float uScale;',
    'varying float vCh;',
    'void main(){vec2 p=a.xy/uRes*2.-1.;gl_Position=vec4(p.x,-p.y,0.,1.);gl_PointSize=max(1.,a.z*2.*uScale);vCh=a.w;}'
  ].join('\n');
  var FS_SPLAT = [
    'precision mediump float;',
    'varying float vCh;uniform float uGain;',
    'void main(){vec2 c=gl_PointCoord*2.-1.;float r2=dot(c,c);if(r2>1.)discard;float f=1.-r2;f=f*f*f*uGain;',
    'vec4 m=vCh<.5?vec4(1.,0.,0.,0.):(vCh<1.5?vec4(0.,1.,0.,0.):(vCh<2.5?vec4(0.,0.,1.,0.):vec4(0.,0.,0.,1.)));',
    'gl_FragColor=m*f;}'
  ].join('\n');
  var VS_QUAD = 'attribute vec2 p;varying vec2 vUv;void main(){vUv=vec2(p.x*.5+.5,.5-p.y*.5);gl_Position=vec4(p,0.,1.);}';
  var FS_COMP = [
    'precision highp float;',
    'varying vec2 vUv;',
    'uniform sampler2D uD,uSkB,uSkF,uFa,uPool,uOp;',
    'uniform vec2 uRes,uTx,uSkRot,uSplit;',
    'uniform vec4 uSkC,uFaR,uPoolR,uMouth,uLv,uLv2;',
    'uniform vec4 uHole[8];',
    'uniform float uT,uTh,uNs,uS;',
    'float band(float t,float c,float w){return 1.-smoothstep(0.,w,abs(t-c));}',
    'vec3 goldEnv(vec3 r){',
    '  float t=-r.y*1.1+.06*sin(r.x*2.6+uT*.35);',
    '  vec3 deep=vec3(.42,.29,.043),mid=vec3(.83,.627,.09),hi=vec3(1.,.914,.66),dark=vec3(.2,.13,.02);',
    '  vec3 sky=mix(mid,hi,smoothstep(.02,.5,t));',
    '  sky=mix(sky,mid*.92,smoothstep(.72,1.1,t));',
    '  vec3 ground=mix(dark,deep,smoothstep(-.08,-.85,t));',
    '  vec3 c=mix(ground,sky,smoothstep(-.03,.03,t));',
    '  c=mix(c,vec3(1.,.96,.82),band(t,.32,.07)*.85);',
    '  c=mix(c,dark,band(t,.62,.08)*.5);',
    '  return c;',
    '}',
    'vec3 chromeEnv(vec3 r){',
    '  float t=-r.y*1.1;',
    '  vec3 sky=mix(vec3(.4,.41,.4),vec3(.97,.96,.94),smoothstep(0.,.5,t));',
    '  vec3 ground=mix(vec3(.08),vec3(.45),smoothstep(-.05,-.9,t));',
    '  return mix(ground,sky,smoothstep(-.03,.03,t));',
    '}',
    'vec4 liquid(vec4 m,float gold,float ns){',
    '  vec2 dv=vec2(vUv.x,1.-vUv.y);',
    '  float c=dot(texture2D(uD,dv),m);',
    '  float a=smoothstep(uTh*.82,uTh*1.08,c);',
    '  if(a<=0.)return vec4(0.);',
    '  float l=dot(texture2D(uD,dv-vec2(uTx.x,0.)),m),r=dot(texture2D(uD,dv+vec2(uTx.x,0.)),m);',
    '  float u=dot(texture2D(uD,dv+vec2(0.,uTx.y)),m),dn=dot(texture2D(uD,dv-vec2(0.,uTx.y)),m);',
    '  vec2 g=vec2(r-l,dn-u)/uTh;',
    '  float core=smoothstep(uTh,uTh*3.,c);',
    '  vec3 n=normalize(vec3(-g*uNs*ns*(1.-core*.55),1.));',
    '  vec3 vd=normalize(vec3((vUv.x-.5)*.45,(vUv.y-.5)*.8-(gold>.5?.3:0.),-1.));',
    '  vec3 rd=reflect(vd,n);',
    '  vec3 col=gold>.5?goldEnv(rd):chromeEnv(rd);',
    '  vec3 L=normalize(vec3(-.4,-.75,.9));',
    '  float nh=max(dot(n,normalize(L+vec3(0.,0.,1.))),0.);',
    '  col+=(gold>.5?vec3(1.,.95,.78):vec3(1.))*(pow(nh,48.)*.75+pow(nh,10.)*.1);',
    '  return vec4(clamp(col,0.,1.)*a,a);',
    '}',
    // thin film on the chrome (rim rivulets, nose): flat wet gold, lit left edge, shaded right edge
    'vec4 filmL(){',
    '  vec2 dv=vec2(vUv.x,1.-vUv.y);',
    '  float c=texture2D(uD,dv).a;',
    '  float a=smoothstep(uTh*.82,uTh*1.08,c);',
    '  if(a<=0.)return vec4(0.);',
    '  float gx=(texture2D(uD,dv+vec2(uTx.x,0.)).a-texture2D(uD,dv-vec2(uTx.x,0.)).a)/uTh;',
    '  float core=smoothstep(uTh,uTh*2.4,c);',
    '  vec3 col=mix(vec3(.6,.37,.04),vec3(.97,.74,.21),core);',
    '  col=mix(col,vec3(1.,.94,.74),clamp(gx*.8,0.,1.)*.75);',
    '  col=mix(col,vec3(.42,.26,.03),clamp(-gx*.8,0.,1.)*.45);',
    '  return vec4(col*a,a);',
    '}',
    'float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}',
    // beer body seen from the side: s = px below the surface, depth 0 (surface) .. 1 (bottom)
    'vec3 beer(float s,float depth,float slope,vec2 p,float bub,float fw){',
    '  vec3 hi=vec3(.99,.8,.3),mid=vec3(.87,.6,.1),deep=vec3(.55,.33,.035);',
    '  vec3 c=mix(hi,mid,smoothstep(0.,.3,depth));',
    '  c=mix(c,deep,smoothstep(.3,1.,depth));',
    '  c+=.06*sin(p.x*.05/uS+uT*1.2+depth*5.)*(1.-depth);',
    '  vec2 q=vec2(p.x/(7.*uS),(p.y+uT*26.*uS)/(7.*uS));vec2 id=floor(q);',
    '  if(hash(id)>1.-bub){vec2 f=fract(q)-vec2(.5+.4*(hash(id+3.1)-.5),.5);float bd=length(f)*7.;c+=vec3(.32,.27,.15)*(1.-smoothstep(.5,1.4,bd))*(1.-depth*.5);}',
    '  float foam=1.-smoothstep(1.3*uS*fw,3.4*uS*fw,s);',
    '  c=mix(c,vec3(1.,.955,.82),foam*.93);',
    '  c+=vec3(.22,.17,.08)*clamp(abs(slope)*1.6,0.,1.)*(1.-smoothstep(0.,7.*uS,s));',
    '  return clamp(c,0.,1.);',
    '}',
    'vec4 spr(sampler2D t,vec4 R,vec2 p){vec2 q=(p-R.xy)/R.zw;if(q.x<0.||q.y<0.||q.x>1.||q.y>1.)return vec4(0.);return texture2D(t,q);}',
    // the skull sprite is drawn rotated about its centre (uSkC = centre, size; uSkRot = cos, sin)
    'vec2 skUv(vec2 p){vec2 dd=p-uSkC.xy;vec2 l=vec2(uSkRot.x*dd.x-uSkRot.y*dd.y,uSkRot.y*dd.x+uSkRot.x*dd.y);return l/uSkC.zw+.5;}',
    'vec4 sprK(sampler2D t,vec2 p){vec2 q=skUv(p);if(q.x<0.||q.y<0.||q.x>1.||q.y>1.)return vec4(0.);return texture2D(t,q);}',
    'void main(){',
    '  vec2 p=vUv*uRes;',
    '  vec2 disp=vec2(0.);float cut=0.,rim=0.,shade=0.;',
    '  for(int i=0;i<8;i++){',
    '    vec4 h=uHole[i];',
    '    if(h.z<0.)continue;',
    '    vec2 dp=p-h.xy;float dd=length(dp)+.001;vec2 dir=dp/dd;',
    '    float age=h.z,heal=1.-smoothstep(.35,2.6,age);',
    '    float ang=atan(dp.y,dp.x);',
    '    float rn=h.w*heal*(1.+.2*sin(ang*5.+age*6.)+.12*sin(ang*3.-age*4.));',
    '    float amp=exp(-age*1.6)*exp(-dd/(h.w*6.));',
    '    disp+=dir*sin(dd*.3-age*18.)*amp*3.2;',
    '    float ring=1.-smoothstep(rn,rn*2.6+2.,dd);',
    '    disp-=dir*ring*rn*.6;',
    '    cut=max(cut,1.-smoothstep(rn*.78,rn,dd));',
    '    rim=max(rim,exp(-pow((dd-rn)/(1.1+rn*.2),2.))*heal);',
    '    shade=max(shade,ring*.4*heal);',
    '  }',
    '  vec2 ps=p-disp;',
    '  vec4 sb=sprK(uSkB,ps),sf=sprK(uSkF,ps),fa=spr(uFa,uFaR,ps);',
    '  vec3 rimc=vec3(1.,.99,.95)*rim;',
    '  sb.rgb=sb.rgb*(1.-shade)+rimc*sb.a;sb*=1.-cut;',
    '  sf.rgb=sf.rgb*(1.-shade)+rimc*sf.a;sf*=1.-cut;',
    '  fa.rgb=fa.rgb*(1.-shade)+rimc*fa.a;fa*=1.-cut;',
    '  vec4 back=liquid(vec4(1.,0.,0.,0.),1.,1.);',
    '  vec4 front=liquid(vec4(0.,1.,0.,0.),1.,1.);',
    '  vec4 silver=liquid(vec4(0.,0.,1.,0.),0.,1.);',
    '  vec4 film=uLv2.w>.5?filmL():vec4(0.);',
    '  vec4 col=sb;',
    // beer collected in the mouth, behind the teeth
    '  if(uMouth.w>.5&&sb.a>.01){',
    '    float ly=uMouth.x+(1.1*sin(p.x*.08/uS+uT*2.4)+.6*sin(p.x*.19/uS-uT*3.1))*uS;',
    '    ly+=uMouth.z*2.2*uS*exp(-pow((p.x-uMouth.y)/(11.*uS),2.))*(.4+.6*sin(uT*21.));',
    '    float s=p.y-ly;',
    '    float a=smoothstep(-.7,.7,s)*sb.a;',
    '    if(a>0.){vec3 bc=beer(s,clamp(s/(52.*uS),0.,1.),0.,p,.04,1.)*.93;col=vec4(bc*a,a)+col*(1.-a);}',
    '  }',
    // open cranium: live carbonated beer inside every opening, below that opening's waterline (behind the front
    // shell): wobbling + sloshing surface (hardest in the mouth), an irregular foamy head with cells, and three
    // layers of rising bubbles, so a rising level never reads as a flat plane being wiped upward
    '  if(uLv2.w>.5&&sb.a>.01){',
    '    vec2 q=skUv(ps);vec4 om=sprK(uOp,ps);',
    '    float m=0.,ly=1e5,dep=46.*uS,fw=2.2,wv=2.2,sh=.9,sl=0.,oid=0.;',
    '    if(om.r>.02){m=om.r;ly=uLv.x;dep=110.*uS;fw=3.;wv=2.4;sh=.97;oid=1.;}',
    '    else if(om.g>.02){m=om.g;ly=q.x<uSplit.x?uLv.y:uLv.z;oid=q.x<uSplit.x?2.:2.6;}',
    '    else if(om.b>.02){m=om.b;if(q.y<uSplit.y){ly=uLv.w;oid=3.;wv=1.4;}else{ly=uLv2.x;dep=64.*uS;wv=2.6;fw=2.7;sl=1.;oid=4.;}}',
    '    if(m>0.&&ly<9e4){',
    '      float t=uT;',
    '      ly+=wv*(1.3*sin(p.x*.07/uS+t*2.6+oid*1.7)+.8*sin(p.x*.17/uS-t*3.7+oid)+.45*sin(p.x*.33/uS+t*6.1))*uS;',
    '      ly+=sl*(5.5*sin(p.x*.022/uS-t*2.3)+2.6*sin(p.x*.05/uS+t*3.4)+1.4*sin(p.x*.11/uS-t*5.2))*uS;',
    // scalloped foam top: a row of foam bubbles (domes) riding on the surface
    '      float cw=(5.5+2.*sin(oid*2.1))*uS,fx=(p.x+t*3.*uS*sin(oid+1.))/cw,fc=fract(fx)-.5,fh=hash(vec2(floor(fx),oid));',
    '      float dome=(1.2+2.2*fh)*uS*sqrt(max(0.,1.-4.*fc*fc));',
    '      ly-=dome;',
    '      if(om.r>.02)ly+=uLv2.z*2.6*uS*exp(-pow((p.x-uLv2.y)/(12.*uS),2.))*(.4+.6*sin(uT*21.));',
    '      float s=p.y-ly;float a=smoothstep(-.7,.7,s)*clamp(m*1.15,0.,1.)*sb.a;',
    '      if(a>0.){',
    '        float fwx=fw*(.8+.35*sin(p.x*.09/uS+t*1.3+oid)+.2*sin(p.x*.23/uS-t*2.1));',
    '        vec3 bc=beer(s,clamp(s/dep,0.,1.),0.,p,0.,fwx)*sh;',
    // carbonation: 3 parallax layers of rising bubbles below the head, bright rim + lighter core
    '        float head=4.*uS*fwx;',
    '        for(int L=0;L<3;L++){',
    '          float fl=float(L),cs=(4.4+fl*3.2)*uS,sp=(60.+fl*55.)*uS;',
    '          vec2 bg=vec2(p.x/cs+fl*7.13+oid*3.1,(p.y+t*sp)/cs);vec2 id=floor(bg);',
    '          float bh=hash(id+fl*13.7);',
    '          if(bh<.42)continue;',
    '          vec2 bf=fract(bg)-.5;bf.x+=.2*sin(t*4.+bh*31.+id.y*.7);',
    '          float br=.14+.16*hash(id+3.3),bd=length(bf);',
    '          float brim=smoothstep(br+.09,br+.01,bd)*(1.-smoothstep(br-.02,br-.11,bd));',
    '          float bcore=smoothstep(br,br-.08,bd);',
    '          float vis=smoothstep(head,head+3.*uS,s)*(.55+.45*(1.-fl*.3));',
    '          bc=mix(bc,vec3(1.,.97,.84),brim*vis*.9);',
    '          bc=mix(bc,bc*1.12+vec3(.05,.04,.01),bcore*vis*.6);',
    '          bc=mix(bc,vec3(1.),smoothstep(.07,.0,length(bf+vec2(br*.35,br*.35)))*vis*.8);',
    '        }',
    // foamy head: cells of lighter foam, wobbling with the surface
    '        float fz=1.-smoothstep(head*.75,head*1.15,s);',
    '        vec2 fq=vec2(p.x+sin(t*1.7+p.y*.2/uS)*1.5*uS,p.y+t*5.*uS)/(3.4*uS);vec2 fid=floor(fq);vec2 ff=fract(fq)-.5;',
    '        float fr=.34+.14*hash(fid),cell=smoothstep(fr,fr-.22,length(ff));',
    '        bc=mix(bc,mix(vec3(.95,.87,.66),vec3(1.,.985,.94),cell),fz*.95);',
    '        col=vec4(bc*a,a)+col*(1.-a);',
    '      }',
    '    }',
    '  }',
    '  col=back+col*(1.-back.a);',
    '  col=sf+col*(1.-sf.a);',
    '  col=film+col*(1.-film.a);',
    '  col=front+col*(1.-front.a);',
    // the pool along the bottom of the frame
    '  if(uPoolR.w>.5&&p.x>uPoolR.x&&p.x<uPoolR.y&&p.y>uPoolR.z-140.*uS){',
    '    float pu=(p.x-uPoolR.x)/(uPoolR.y-uPoolR.x);',
    '    float hh=texture2D(uPool,vec2(pu,.5)).r*127.5;',
    '    if(hh>.25){',
    '      float s=p.y-(uPoolR.z-hh);',
    '      float a=smoothstep(-.7,.7,s);',
    '      if(a>0.){',
    '        float du=4./(uPoolR.y-uPoolR.x);',
    '        float sl=(texture2D(uPool,vec2(pu+du,.5)).r-texture2D(uPool,vec2(pu-du,.5)).r)*127.5/8.;',
    '        vec3 bc=beer(s,clamp(s/max(hh,1.),0.,1.),sl,p,.07,1.);',
    '        col=vec4(bc*a,a)+col*(1.-a);',
    '      }',
    '    }',
    '  }',
    '  col=fa+col*(1.-fa.a);',
    '  col=silver+col*(1.-silver.a);',
    '  gl_FragColor=col;',
    '}'
  ].join('\n');

  function sh(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  }
  function prog(gl, vs, fs) {
    var p = gl.createProgram();
    gl.attachShader(p, sh(gl, gl.VERTEX_SHADER, vs)); gl.attachShader(p, sh(gl, gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
    return p;
  }
  function texFrom(gl, img) {
    var t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  }

  // stage px -> skull sprite uv (inverse of the rotation about the sprite centre)
  function toSprite(K, X, Y) {
    var dx = X - K.cx, dy = Y - K.cy;
    return [(K.c * dx - K.s * dy) / K.w + 0.5, (K.s * dx + K.c * dy) / K.h + 0.5];
  }
  // stage-space masks of the rotated skull: mouth hole + front chrome, per-column top of the hole
  function buildMasks(G) {
    var K = G.skull, an = G.an, box = G.skullBox;
    var cell = Math.max(1.5, 1.6 * G.S), x0 = box[0], y0 = box[1];
    var mw = Math.ceil((box[2] - x0) / cell) + 1, mh = Math.ceil((box[3] - y0) / cell) + 1;
    var hole = new Uint8Array(mw * mh), front = new Uint8Array(mw * mh), top = new Float32Array(mw);
    var i, j;
    for (i = 0; i < mw; i++) top[i] = 1e9;
    for (j = 0; j < mh; j++) {
      for (i = 0; i < mw; i++) {
        var X = x0 + (i + 0.5) * cell, Y = y0 + (j + 0.5) * cell, uv = toSprite(K, X, Y);
        if (sampleMap(an.back, uv[0], uv[1]) > 127) { hole[j * mw + i] = 1; if (Y < top[i]) top[i] = Y; }
        if (sampleMap(an.front, uv[0], uv[1]) > 127) front[j * mw + i] = 1;
      }
    }
    return {
      hole: hole, front: front,
      at: function (m, x, y) {
        var a = ((x - x0) / cell) | 0, b = ((y - y0) / cell) | 0;
        if (x < x0 || y < y0 || a >= mw || b >= mh) return 0;
        return m[b * mw + a];
      },
      holeTopAt: function (x) {
        var a = ((x - x0) / cell) | 0;
        return (x < x0 || a >= mw) ? 1e9 : top[a];
      }
    };
  }

  // Shader compile/link without blocking: with KHR_parallel_shader_compile the driver compiles on its own threads;
  // prepare() starts it, ready() polls it, create() finishes (the link-status query no longer stalls the page).
  var GL_OPTS = { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'high-performance' };
  function startProg(gl, vs, fs) {
    var p = gl.createProgram(), a = gl.createShader(gl.VERTEX_SHADER), b = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(a, vs); gl.compileShader(a); gl.shaderSource(b, fs); gl.compileShader(b);
    gl.attachShader(p, a); gl.attachShader(p, b); gl.linkProgram(p);
    return { p: p, vs: a, fs: b };
  }
  function endProg(gl, o) {
    if (!gl.getProgramParameter(o.p, gl.LINK_STATUS)) throw new Error(gl.getShaderInfoLog(o.fs) || gl.getShaderInfoLog(o.vs) || gl.getProgramInfoLog(o.p));
    return o.p;
  }
  function prepare(opts) {
    var gl = opts.canvas.getContext('webgl', GL_OPTS);
    if (!gl) return null;
    var par = gl.getExtension('KHR_parallel_shader_compile'), a = startProg(gl, VS_SPLAT, FS_SPLAT), b = startProg(gl, VS_QUAD, FS_COMP);
    return {
      ready: function () { return !par || (gl.getProgramParameter(a.p, par.COMPLETION_STATUS_KHR) && gl.getProgramParameter(b.p, par.COMPLETION_STATUS_KHR)); },
      create: function () { return create(opts, { gl: gl, a: a, b: b }); }
    };
  }

  function create(opts, pre) {
    var canvas = opts.canvas;
    var gl = pre ? pre.gl : canvas.getContext('webgl', GL_OPTS);
    if (!gl) return null;
    var pSplat, pComp;
    try {
      if (pre) { pSplat = endProg(gl, pre.a); pComp = endProg(gl, pre.b); }
      else { pSplat = prog(gl, VS_SPLAT, FS_SPLAT); pComp = prog(gl, VS_QUAD, FS_COMP); }
    }
    catch (e) { console.warn('[pz-pour]', e.message); return null; }

    // density target: half-float when renderable, else 8-bit
    var hf = gl.getExtension('OES_texture_half_float'), hfl = gl.getExtension('OES_texture_half_float_linear');
    gl.getExtension('EXT_color_buffer_half_float');
    var dTex = gl.createTexture(), fb = gl.createFramebuffer(), dType = gl.UNSIGNED_BYTE, dW = 2, dH = 2;
    function allocDensity(wd, ht) {
      dW = wd; dH = ht;
      gl.bindTexture(gl.TEXTURE_2D, dTex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      var types = (hf && hfl) ? [hf.HALF_FLOAT_OES, gl.UNSIGNED_BYTE] : [gl.UNSIGNED_BYTE];
      for (var t = 0; t < types.length; t++) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, wd, ht, 0, gl.RGBA, types[t], null);
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, dTex, 0);
        if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE) { dType = types[t]; break; }
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    var quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    var maxP = opts.max || 1200;
    var pbuf = gl.createBuffer(), pdata = new Float32Array(maxP * 4 * 3); // + 2 trail splats per falling pour particle
    gl.bindBuffer(gl.ARRAY_BUFFER, pbuf);
    gl.bufferData(gl.ARRAY_BUFFER, pdata.byteLength, gl.DYNAMIC_DRAW);
    var aSplat = gl.getAttribLocation(pSplat, 'a'), aQuad = gl.getAttribLocation(pComp, 'p');
    var US = {}, UC = {};
    ['uRes', 'uScale', 'uGain'].forEach(function (n) { US[n] = gl.getUniformLocation(pSplat, n); });
    ['uD', 'uSkB', 'uSkF', 'uFa', 'uPool', 'uOp', 'uRes', 'uTx', 'uSkRot', 'uSplit', 'uSkC', 'uFaR', 'uPoolR', 'uMouth', 'uLv', 'uLv2', 'uHole', 'uT', 'uTh', 'uNs', 'uS'].forEach(function (n) { UC[n] = gl.getUniformLocation(pComp, n); });

    // pool heights: one byte per column, 0.5px steps
    var poolTex = gl.createTexture(), poolBytes = new Uint8Array(1), poolAlloc = 0;
    gl.bindTexture(gl.TEXTURE_2D, poolTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, 1, 1, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, poolBytes);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    var sim = new Sim(maxP);
    var texSkB = null, texSkF = null, texFa = null, texOp = null, amFa = null;
    var empty = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, empty);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
    var G = null, MK = null; // geometry in stage css px, stage-space skull masks
    var holes = [], holeData = new Float32Array(32);
    var raf = 0, running = false, dead = false, last = 0, acc = 0, t0 = performance.now(), frameTimes = [], started = 0, climbs = 0;
    var dprMax = Math.min(opts.dprMax || 1.5, w.devicePixelRatio || 1), dpr = dprMax;
    var fps = opts.fps || 60, lastDraw = 0; // R10: drawn at up to 60 fps (never the display rate), 30 in idle mode
    var drawn = 0, ialpha = 1; // R10: interpolation between the last two sim steps (the sim runs at a fixed 90 / 60 Hz)

    // skull back (interiors / mouth, optional) + skull front (shell) + faucet (+ packed openings mask, R3)
    function setSprites(backImg, frontImg, faImg, opImg) {
      [texSkB, texSkF, texFa, texOp].forEach(function (t) { if (t) gl.deleteTexture(t); });
      texSkB = backImg ? texFrom(gl, backImg) : null; texSkF = texFrom(gl, frontImg); texFa = texFrom(gl, faImg);
      texOp = opImg ? texFrom(gl, opImg) : null;
      amFa = alphaMap(faImg, 160);
    }

    function layout(geom) {
      var S = geom.S;
      var mf = sim.mouth && sim.mouth.cap ? sim.mouth.vol / sim.mouth.cap : 0;
      var pf = sim.pool && sim.pool.cap ? sim.pool.vol / sim.pool.cap : 0;
      var vh = sim.vessel ? sim.vLevel() : 0;
      G = geom;
      // resizing the canvas or the density target stalls the GPU (100+ ms at 2560): only when the size changes
      var cw = Math.max(2, Math.round(geom.W * dpr)), chh = Math.max(2, Math.round(geom.H * dpr));
      if (canvas.width !== cw) canvas.width = cw;
      if (canvas.height !== chh) canvas.height = chh;
      var dw = Math.max(2, Math.round(geom.W * dpr * 0.5)), dh = Math.max(2, Math.round(geom.H * dpr * 0.5));
      if (dw !== dW || dh !== dH) allocDensity(dw, dh);
      MK = sim.masks = buildMasks(geom);
      var rate = geom.rate || 95, hl = geom.hole;
      sim.mouth = null; sim.vessel = null;
      if (geom.vessel) {
        // open cranium: keep the level across relayouts, rebuild the openings in stage px
        var gv = geom.vessel, K = geom.skull, V = { rate: rate, segs: gv.segs, vol: rate * volOf(gv.segs, vh), h: vh, hits: 0, rip: 0, ripX: 0, ops: [], cr: null,
          an: geom.an, sk: [K.cx - K.w / 2, K.cy - K.h / 2, K.w, K.h] };
        gv.ops.forEach(function (o) {
          var wsum = 0;
          o.spills.forEach(function (s) { wsum += s.w; });
          var op = {
            key: o.key, hLip: o.hLip, hFull: o.hFull, yA: o.yA, yF: o.yF, kappa: o.kappa, exp: o.exp || 1, cap: o.cap || 9,
            spills: o.spills, wsum: wsum || 1,
            fv: o.fv, rad: o.rad, jit: o.jit, vx: o.vx, pour: !!o.pour, bb: o.bb || null, acc: 0, out: 0, wl: 1e5, fz: 0
          };
          V.ops.push(op);
          if (o.key === 'cranium') V.cr = op;
        });
        sim.vessel = V.cr ? V : null;
      } else if (hl) {
        sim.mouth = {
          cap: rate * 6.3, vol: mf * rate * 6.3, ratio: 0.32, ovAcc: 0, hits: 0, rip: 0,
          bottomY: hl.bottomY, spillY: hl.spillY, spills: hl.spills
        };
      }
      var dx = Math.max(4, 3 * S), p0 = geom.pool[0], p1 = geom.pool[1], N = Math.max(3, Math.ceil((p1 - p0) / dx) + 1);
      var c = 130 * S / 90 / dx;
      sim.pool = {
        x0: p0, x1: p1, cx: geom.pool[2], capH: geom.poolCap, cap: rate * 0.5 * 30, vol: pf * rate * 0.5 * 30,
        dx: dx, N: N, h: new Float32Array(N), vv: new Float32Array(N), c2: Math.min(0.5, c * c), level: 0, curL: 0, curR: 0, v: 0
      };
      if (poolAlloc !== N) { poolBytes = new Uint8Array(N); poolAlloc = N; }
      sim.g = {
        S: S, W: geom.W, H: geom.H, sx: geom.spout.x, sy: geom.spout.y, sw: geom.spout.w || 2.2 * S, rate: rate, mobile: !!geom.mobile,
        emit: true, floorY: geom.floorY, minX: geom.minX, maxX: geom.maxX
      };
      sim.poolShape();
      if (!running) render();
    }

    function skullAlpha(x, y) {
      if (!G) return 0;
      var uv = toSprite(G.skull, x, y);
      return Math.max(sampleMap(G.an.front, uv[0], uv[1]), sampleMap(G.an.back, uv[0], uv[1])) / 255;
    }
    function faucetAlpha(x, y) {
      if (!G || !amFa) return 0;
      var R = G.faucet;
      return sampleMap(amFa, (x - R[0]) / R[2], (y - R[1]) / R[3]) / 255;
    }
    // open cranium: the beer inside an opening under (x, y), or null
    function beerAt(x, y) {
      var V = sim.vessel;
      if (!V || !G || !G.an || !G.an.open) return null;
      var uv = toSprite(G.skull, x, y), key = openingAt(G.an, uv[0], uv[1]);
      if (!key) return null;
      for (var k = 0; k < V.ops.length; k++) {
        var o = V.ops[k];
        if (o.key === key) { var wl = sim.vWater(o, sim.vLevel()); return y > wl ? { op: o, wl: wl } : null; }
      }
      return null;
    }
    function hit(x, y) {
      if (!G) return null;
      // the visible stream sits in front of the face, so it wins over the chrome under it (tight radius on the
      // open skull: rivulets cover the face, the chrome between them still takes the bullet)
      if (sim.nearest(x, y, (sim.vessel ? 5 : 11) * G.S)) return 'stream';
      if (faucetAlpha(x, y) > 0.35) return 'faucet';
      if (beerAt(x, y)) return 'beer';
      if (MK && sim.mouth && sim.mouth.vol > 0 && MK.at(MK.hole, x, y) && y > sim.mouthLevel()) return 'mouth';
      if (skullAlpha(x, y) > 0.35) return 'skull';
      if (sim.pool && sim.pool.level && y > G.floorY - sim.poolSurf(x) - 5 * G.S && y <= G.floorY) return 'pool';
      if (sim.nearest(x, y, 26 * G.S)) return 'stream';
      return null;
    }
    function droplets(x, y, n, sp) {
      for (var i = 0; i < n; i++) {
        var a = rnd(-Math.PI * 0.85, -Math.PI * 0.15), v = rnd(0.4, 1) * sp;
        var k = sim.spawn(x + rnd(-4, 4) * G.S, y - rnd(1, 4) * G.S, Math.cos(a) * v, Math.sin(a) * v, K_FREE, 0);
        if (k >= 0) sim.size[k] = rnd(0.5, 0.9);
      }
    }
    function shoot(x, y) {
      var what = hit(x, y);
      if (!what) return null;
      if (what === 'stream') {
        sim.burst(x, y, 60 * G.S, 900 * G.S);
      } else if (what === 'pool') {
        sim.poolKick(x, -9 * G.S);
        droplets(x, G.floorY - sim.poolSurf(x), 14, 520 * G.S);
      } else if (what === 'mouth') {
        sim.mouth.rip = 1;
        droplets(x, sim.mouthLevel(), 10, 380 * G.S);
      } else if (what === 'beer') {
        var bb = beerAt(x, y);
        if (bb) {
          if (bb.op.key === 'cranium') { sim.vessel.rip = 1; sim.vessel.ripX = x; }
          droplets(x, bb.wl, 10, 380 * G.S);
        }
      } else {
        if (holes.length >= 8) holes.shift();
        holes.push({ x: x, y: y, t: performance.now(), r: rnd(9, 13) * G.S });
        // small silver splash out of the wound
        for (var i = 0; i < 16; i++) {
          var a = rnd(-Math.PI, 0) + rnd(-0.4, 0.4), sp = rnd(160, 520) * G.S;
          sim.spawn(x + rnd(-3, 3), y + rnd(-3, 3), Math.cos(a) * sp, Math.sin(a) * sp, K_SILVER, rnd(0.6, 1.3));
        }
      }
      if (!running) { render(); }
      return what;
    }

    function uploadPool(now) {
      var P = sim.pool;
      if (!P) return false;
      sim.poolShape();
      if (!P.level) return false;
      var t = (now - t0) / 1000, S = G.S, amb = Math.min(1.5 * S, 0.07 * P.level);
      for (var i = 0; i < P.N; i++) {
        var X = P.x0 + i * P.dx, e = sim.poolEdge(X);
        var hgt = e ? (P.level + P.h[i] + amb * (Math.sin(X * 0.035 / S + t * 1.6) + 0.55 * Math.sin(X * 0.09 / S - t * 2.4))) * e : 0;
        poolBytes[i] = clamp(Math.round(hgt * 2), 0, 255);
      }
      gl.bindTexture(gl.TEXTURE_2D, poolTex);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, P.N, 1, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, poolBytes);
      return true;
    }

    // scissor rect from stage css px (top-left origin) to canvas px (bottom-left origin)
    function scissor(x0, y0, x1, y1) {
      var k = canvas.width / G.W, X0 = Math.max(0, Math.floor(x0 * k)), X1 = Math.min(canvas.width, Math.ceil(x1 * k));
      var Y0 = Math.max(0, Math.floor(y0 * k)), Y1 = Math.min(canvas.height, Math.ceil(y1 * k));
      gl.scissor(X0, canvas.height - Y1, Math.max(0, X1 - X0), Math.max(0, Y1 - Y0));
    }
    function render() {
      if (!G) return;
      var now = performance.now();
      // particles -> buffer
      var n = 0, S = G.S, base = 14 * S, bx0 = 1e9, by0 = 1e9, bx1 = -1e9, by1 = -1e9;
      for (var i = 0; i < sim.max; i++) {
        if (!sim.alive[i]) continue;
        var px_ = sim.x[i], py_ = sim.y[i];
        if (px_ < bx0) bx0 = px_; if (px_ > bx1) bx1 = px_; if (py_ < by0) by0 = py_; if (py_ > by1) by1 = py_;
        var k = sim.kind[i], ch;
        if (k === K_SILVER) ch = 2;
        else if ((k === K_STREAM || k === K_FIZZ) && sim.vessel) ch = 0; // open cranium: stream + bubbles sit behind the front shell
        else if ((k === K_FILM || k === K_POUR) && sim.vessel) ch = 3; // outflow: wet film on the chrome + clean pours, smooth shading
        else if (k === K_STREAM && MK && sim.y[i] > MK.holeTopAt(sim.x[i])) ch = 0; // inside the mouth, behind the teeth
        else ch = 1;
        var o = n * 4;
        var ix = sim.px[i] + (sim.x[i] - sim.px[i]) * ialpha, iy = sim.py[i] + (sim.y[i] - sim.py[i]) * ialpha;
        pdata[o] = ix; pdata[o + 1] = iy;
        pdata[o + 2] = base * sim.size[i] * sim.rad[i] * (k === K_SILVER ? 0.7 : (k === K_FILM || k === K_POUR ? 0.78 : 1)); pdata[o + 3] = ch;
        n++;
        if (k === K_POUR) {
          // falling pour: two trail splats back along the velocity so the stream reads continuous, not beaded
          for (var tj = 1; tj <= 2; tj++) {
            var q4 = n * 4, back = tj * 0.0105;
            pdata[q4] = ix - sim.vx[i] * back; pdata[q4 + 1] = iy - sim.vy[i] * back;
            pdata[q4 + 2] = pdata[o + 2] * 0.92; pdata[q4 + 3] = ch;
            n++;
          }
        }
      }
      // splat pass
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.viewport(0, 0, dW, dH);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      if (n) {
        gl.useProgram(pSplat);
        gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE);
        gl.bindBuffer(gl.ARRAY_BUFFER, pbuf);
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, pdata.subarray(0, n * 4));
        gl.enableVertexAttribArray(aSplat);
        gl.vertexAttribPointer(aSplat, 4, gl.FLOAT, false, 0, 0);
        gl.uniform2f(US.uRes, G.W, G.H);
        gl.uniform1f(US.uScale, dW / G.W);
        gl.uniform1f(US.uGain, dType === gl.UNSIGNED_BYTE ? 0.3 : 0.5);
        gl.drawArrays(gl.POINTS, 0, n);
        gl.disable(gl.BLEND);
        gl.disableVertexAttribArray(aSplat);
      }
      var poolOn = uploadPool(now);
      // composite
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(pComp);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(aQuad);
      gl.vertexAttribPointer(aQuad, 2, gl.FLOAT, false, 0, 0);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, dTex);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, texSkB || empty);
      gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, texSkF || empty);
      gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, texFa || empty);
      gl.activeTexture(gl.TEXTURE4); gl.bindTexture(gl.TEXTURE_2D, poolTex);
      gl.activeTexture(gl.TEXTURE5); gl.bindTexture(gl.TEXTURE_2D, texOp || empty);
      gl.uniform1i(UC.uD, 0); gl.uniform1i(UC.uSkB, 1); gl.uniform1i(UC.uSkF, 2); gl.uniform1i(UC.uFa, 3); gl.uniform1i(UC.uPool, 4); gl.uniform1i(UC.uOp, 5);
      gl.uniform2f(UC.uRes, G.W, G.H);
      gl.uniform2f(UC.uTx, 1.5 / dW, 1.5 / dH);
      gl.uniform4f(UC.uSkC, G.skull.cx, G.skull.cy, G.skull.w, G.skull.h);
      gl.uniform2f(UC.uSkRot, G.skull.c, G.skull.s);
      gl.uniform4f(UC.uFaR, G.faucet[0], G.faucet[1], G.faucet[2], G.faucet[3]);
      var P = sim.pool, M = sim.mouth;
      gl.uniform4f(UC.uPoolR, P ? P.x0 : 0, P ? P.x0 + (P.N - 1) * P.dx : 1, G.floorY, poolOn ? 1 : 0);
      gl.uniform4f(UC.uMouth, sim.mouthLevel(), G.spout.x, M ? M.rip : 0, M && M.vol > 0.5 && texSkB ? 1 : 0);
      // open cranium: waterline per opening (cranium, eye_l, eye_r, nose | mouth), ripple, on
      var V = sim.vessel, wl = { cranium: 1e5, eye_l: 1e5, eye_r: 1e5, nose: 1e5, mouth: 1e5 };
      if (V) { var lv = sim.vLevel(); V.ops.forEach(function (o) { wl[o.key] = sim.vWater(o, lv); }); }
      gl.uniform4f(UC.uLv, wl.cranium, wl.eye_l, wl.eye_r, wl.nose);
      gl.uniform4f(UC.uLv2, wl.mouth, V ? V.ripX : 0, V ? V.rip : 0, V && texOp ? 1 : 0);
      gl.uniform2f(UC.uSplit, G.an && G.an.split ? G.an.split[0] : 0, G.an && G.an.split ? G.an.split[1] : 0);
      for (var h = 0; h < 8; h++) {
        var HO = holes[h];
        if (HO) {
          var age = (now - HO.t) / 1000;
          holeData[h * 4] = HO.x; holeData[h * 4 + 1] = HO.y; holeData[h * 4 + 2] = age; holeData[h * 4 + 3] = HO.r;
        } else { holeData[h * 4 + 2] = -1; }
      }
      gl.uniform4fv(UC.uHole, holeData);
      gl.uniform1f(UC.uT, (now - t0) / 1000);
      var gain = dType === gl.UNSIGNED_BYTE ? 0.3 : 0.5;
      gl.uniform1f(UC.uTh, gain * 0.55);
      gl.uniform1f(UC.uNs, 1.8);
      gl.uniform1f(UC.uS, S);
      // R8: the composite shader is the GPU cost; run it only over (a) the centerpiece + every particle + bullet
      // holes and (b) the pool strip, instead of the whole hero
      var pad = 34 * S, sk = G.skullBox, fa = G.faucet;
      var ax0 = Math.min(sk[0], fa[0], bx0) - pad, ay0 = Math.min(sk[1], fa[1], by0) - pad;
      var ax1 = Math.max(sk[2], fa[0] + fa[2], bx1) + pad, ay1 = Math.max(sk[3], fa[1] + fa[3], by1) + pad;
      for (var hq = 0; hq < holes.length; hq++) {
        var HQ = holes[hq]; ax0 = Math.min(ax0, HQ.x - 60 * S); ax1 = Math.max(ax1, HQ.x + 60 * S); ay0 = Math.min(ay0, HQ.y - 60 * S); ay1 = Math.max(ay1, HQ.y + 60 * S);
      }
      gl.enable(gl.SCISSOR_TEST);
      scissor(ax0, ay0, ax1, ay1);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      if (poolOn || P) {
        scissor(P ? P.x0 - pad : 0, G.floorY - 150 * S, P ? P.x0 + (P.N - 1) * P.dx + pad : G.W, G.floorY + 2);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
      gl.disable(gl.SCISSOR_TEST);
      gl.disableVertexAttribArray(aQuad);
      // expire healed holes
      holes = holes.filter(function (o) { return now - o.t < 3200; });
    }

    // pace the loop with a timer and only then ask for an animation frame: requesting a frame every vsync (and
    // skipping most of them) still makes the browser produce a frame per vsync, 144-360 times a second on
    // high-refresh screens
    var tmr = 0;
    function next() {
      var wait = 1000 / fps - (performance.now() - last) - 4;
      if (wait > 2) tmr = setTimeout(function () { tmr = 0; if (running && !dead) raf = requestAnimationFrame(frame); }, wait);
      else raf = requestAnimationFrame(frame);
    }
    function frame(now) {
      raf = 0;
      if (dead || !running) return;
      // R8 idle budget: draw at most `fps` times a second (30, 15 in breathing mode); the sim catches up with the
      // elapsed time in fixed steps, so the pour runs at the same speed at any frame rate
      if (now - last < 1000 / fps - 1) { next(); return; }
      var raw = (now - last) / 1000, dt = Math.min(0.1, raw), target = 1 / fps;
      last = now;
      // adaptive resolution: ignore the first 2.5 s after (re)start (page-load jank) and single stalls (tab switch,
      // capture, GC); drop 0.25 when a window of frames runs well behind the cap, climb back when it keeps up
      if (raw < 0.1 && now - started > 2500) frameTimes.push(dt);
      if (frameTimes.length >= 60) {
        var avg = frameTimes.reduce(function (a, b) { return a + b; }, 0) / frameTimes.length;
        frameTimes.length = 0;
        if (avg > target * 1.45 && dpr > 0.75) { dpr = Math.max(0.75, dpr - 0.25); if (G) layout(G); }
        else if (avg < target * 1.12 && dpr < dprMax && climbs < 3) { dpr = Math.min(dprMax, dpr + 0.25); climbs++; if (G) layout(G); }
      }
      acc += dt;
      if (opts.onFrame) opts.onFrame();
      var mob = G && G.mobile, steps = 0, STEP = mob ? 1 / 60 : 1 / 90, MAXS = Math.ceil(0.1 / STEP);
      while (acc >= STEP && steps < MAXS) { sim.step(STEP); acc -= STEP; steps++; }
      if (steps === MAXS) acc = 0;
      // draw between the previous and the current sim state: smooth motion whatever the step / frame ratio
      ialpha = Math.max(0, Math.min(1, acc / STEP));
      render();
      ialpha = 1;
      lastDraw = now; drawn++;
      next();
    }

    return {
      sim: sim,
      setSprites: setSprites,
      // one texture per call (lets the caller spread the uploads over several tasks)
      setSprite: function (slot, img) {
        var t = img ? texFrom(gl, img) : null;
        if (slot === 'back') { if (texSkB) gl.deleteTexture(texSkB); texSkB = t; }
        else if (slot === 'front') { if (texSkF) gl.deleteTexture(texSkF); texSkF = t; }
        else if (slot === 'faucet') { if (texFa) gl.deleteTexture(texFa); texFa = t; amFa = img ? alphaMap(img, 160) : null; }
        else if (slot === 'op') { if (texOp) gl.deleteTexture(texOp); texOp = t; }
      },
      layout: layout,
      hit: hit,
      shoot: shoot,
      setColliders: function (list) { sim.colliders = list; },
      // R8: frame-rate cap (30 normal, 15 breathing)
      setFps: function (f) { fps = Math.max(5, Math.min(60, f || 60)); },
      running: function () { return running; },
      // beer spilled by something else on the stage (the hero glass): falls clean and joins the pool
      drop: function (x, y, vx, vy, rad) {
        var i = sim.spawn(x, y, vx, vy, K_POUR, 0);
        if (i >= 0) { sim.rad[i] = rad || 0.8; sim.size[i] = 0.9; }
        return i >= 0;
      },
      prewarm: function (seconds) { for (var t = 0; t < seconds; t += 1 / 90) sim.step(1 / 90); render(); },
      // reduced motion: jump to the steady state (skull full and overflowing, pool at its cap), let the
      // outflow run for `seconds` so the rivulets and streams reach the pool, draw one frame
      settle: function (seconds) {
        var V = sim.vessel, i;
        for (i = 0; i < sim.max; i++) sim.kill(i);
        if (V) V.vol = V.rate * volOf(V.segs, G && G.vessel ? G.vessel.hSteady : 1.08);
        if (sim.mouth) sim.mouth.vol = sim.mouth.cap;
        if (sim.pool) sim.pool.vol = sim.pool.cap;
        for (var t = 0; t < (seconds || 3); t += 1 / 60) sim.step(1 / 60);
        render();
      },
      render: render,
      geom: function () { return G; },
      state: function () {
        var n = 0, V = sim.vessel, out = {};
        for (var i = 0; i < sim.max; i++) n += sim.alive[i];
        if (V) V.ops.forEach(function (o) { out[o.key] = o.out; });
        return {
          t: +sim.time.toFixed(2), particles: n, dpr: dpr, fps: fps, drawn: drawn,
          mouth: sim.mouth ? +(sim.mouth.vol / sim.mouth.cap).toFixed(3) : 0,
          level: V ? +sim.vLevel().toFixed(3) : 0, out: out,
          pool: sim.pool ? +(sim.pool.vol / sim.pool.cap).toFixed(3) : 0,
          poolLevel: sim.pool ? +sim.pool.level.toFixed(1) : 0
        };
      },
      start: function () { if (running || dead) return; running = true; last = started = performance.now(); frameTimes.length = 0; raf = requestAnimationFrame(frame); },
      stop: function () { running = false; if (raf) cancelAnimationFrame(raf); raf = 0; clearTimeout(tmr); tmr = 0; },
      destroy: function () {
        dead = true; running = false; if (raf) cancelAnimationFrame(raf); clearTimeout(tmr);
        var lose = gl.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext();
      }
    };
  }

  w.PZPour = { create: create, prepare: prepare, analyze: analyze, analyzeOpen: analyzeOpen, toSprite: toSprite };
})();
